import { parsePhoneNumber } from "awesome-phonenumber";
import { SupportedCountries } from "@/enums/internationalization";

// Comprehensive regex to match various phone extension formats
const PhoneExtensionRegex =
  /(?:[,;]|(?:\s*(?:x|ext\.?|extension|内線)\s*)|(?:\s*#\s*)|(?:\s*p\s*)|(?:\s*;ext\s*=\s*))(\d+)/gi;

const PhoneExtensionPrefix = ";ext=";

export const PhoneExtensionUiPrefix = "x";
export const SID_REGEX = /CA[a-zA-Z0-9]{32}/;

// A "+" means the value is already international. Reformatting every 10-digit
// string as a US national number strips that prefix. Valid numbers whose
// E.164 form is 10 digits, such as the Estonian landline +3726755824, then
// fail when they are parsed with the org's localization country.
const formatNationalTenDigit = (value: string): string | null => {
  if (value.includes("+")) {
    return null;
  }

  const digitsOnly = value.replace(/[^\d]/g, "");
  if (digitsOnly.length !== 10) {
    return null;
  }

  return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
};

export const GetPhoneNumberAndExtension = (
  phone: string
): { phone: string; extension: string } => {
  if (!phone || phone.length === 0) {
    return {
      phone: "",
      extension: "",
    };
  }

  // Find all extension matches
  const extensionMatches = [...phone.matchAll(PhoneExtensionRegex)];

  if (extensionMatches.length === 0) {
    return {
      phone: formatNationalTenDigit(phone) ?? phone.trim(),
      extension: "",
    };
  }

  // Get the last extension match (in case there are multiple)
  const lastMatch = extensionMatches[extensionMatches.length - 1];
  const extension = lastMatch[1]; // The captured group with the extension number

  // Remove ALL extensions from the phone number to get just the base phone
  let phoneWithoutExtensions = phone;
  extensionMatches.forEach((match) => {
    phoneWithoutExtensions = phoneWithoutExtensions.replace(match[0], "");
  });

  // Clean up any extra spaces or formatting
  const cleanedPhone = phoneWithoutExtensions.replace(/\s+/g, " ").trim();

  return {
    phone: formatNationalTenDigit(cleanedPhone) ?? cleanedPhone,
    extension: extension,
  };
};

export const GetPhoneNumberForDb = (
  phoneNumber: string,
  country: SupportedCountries
): string | null => {
  const { phone, extension } = GetPhoneNumberAndExtension(phoneNumber?.trim());
  const parsedNumber = parsePhoneNumber(phone.trim(), { regionCode: country });
  let phoneParsed = parsedNumber?.valid ? parsedNumber.number.e164 : null;

  if (!extension || !phoneParsed) {
    return phoneParsed;
  }

  return phoneParsed + PhoneExtensionPrefix + extension;
};

export const GetPhoneNumberForUI = (
  phoneNumber: string,
  country: SupportedCountries,
  separateExtension?: string
): string => {
  const phoneToUse = `${phoneNumber}${
    separateExtension ? `${PhoneExtensionPrefix}${separateExtension}` : ""
  }`;

  const { phone, extension } = GetPhoneNumberAndExtension(phoneToUse.trim());
  const parsedNumber = parsePhoneNumber(phone, { regionCode: country });

  const NationalOrInternational =
    parsedNumber.regionCode === country
      ? parsedNumber?.number?.national
      : parsedNumber?.number?.international;

  let phoneParsed = NationalOrInternational
    ? NationalOrInternational
    : phone.trim();

  if (!extension) {
    return phoneParsed;
  }

  return `${phoneParsed} ${PhoneExtensionUiPrefix}${extension}`;
};

export const NormalizePhoneNumber = (
  phoneNumber: string,
  country: SupportedCountries
): string => {
  // Remove all non-numeric characters except '+'
  let cleaned = phoneNumber.replace(/[^\d+]/g, "");

  // If the number starts with '+', return it as is
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  const parsedNumber = parsePhoneNumber(cleaned, { regionCode: country });

  if (!parsedNumber.valid) {
    return "";
  }

  return parsedNumber.number.e164;
};
